import { useEffect } from "react";
import { useOverlayDismiss, OVERLAY_PANEL_PROPS } from "@shared-ui/hooks";
import type React from "react";

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** シートの最大高 (vh)。デフォルト 80 */
  maxHeightVh?: number;
  /** 背景オーバーレイをタップしたら閉じる。デフォルト true */
  closeOnBackdropClick?: boolean;
  /**
   * PRODUCT_DEV-631: 重ね順の Tailwind クラス。デフォルト "z-50"。
   * メニュー（MenuSheet / AdminLayout）自体が z-50 のため、その上に開くシートは
   * "z-[60]" 等を渡さないと背面に隠れる（MobileTabBar に同種の z-50 衝突の既往あり）。
   */
  zIndexClass?: string;
}

/**
 * PRODUCT_DEV-414 follow-up #3: 下から立ち上がる bottom sheet 共通コンポーネント。
 * CopyPartnerSelect の `animate-slideUpFromBottom` パターンを共通化し、Preset 等
 * 各種モーダルでスライドアップモーションを統一する。
 */
export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  maxHeightVh = 80,
  closeOnBackdropClick = true,
  zIndexClass = "z-50",
}) => {
  /*
    外側を押して閉じる規則は**共通部品**（PD-710。`@shared-ui/hooks`）。
    以前はオーバーレイの `onClick` で閉じており、**中で文字をドラッグ選択して
    外側で指を離すと閉じて**いた（click は mousedown と mouseup の共通祖先で
    発火するので、パネルの `stopPropagation` を通らない）。
    押した位置と離した位置の**両方がパネルの外**のときだけ閉じる。
  */
  const overlayDismiss = useOverlayDismiss(
    closeOnBackdropClick ? onClose : undefined,
  );
  // Esc キーで閉じる
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 ${zIndexClass} animate-fadeIn`}
      {...overlayDismiss}
    >
      <div
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl pb-safe-bottom overflow-y-auto animate-slideUpFromBottom"
        style={{ maxHeight: `${maxHeightVh}vh` }}
        // **この印が無いとパネルの中を押しても閉じる**（判定は「パネルの外か」で行う）。
        {...OVERLAY_PANEL_PROPS}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
};

export default BottomSheet;
